import React from 'react';
import { Pressable, StyleSheet } from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import { FontSizes } from '@/Utils/Dimensions';
import { MaterialCommunityIcons } from '@expo/vector-icons';

interface WebinarOption {
    batch_code: string;
    level: string;
    tournament_id: number;
    webinar_ids: number[];
    class_days: string[];
    unique_timings: string[];
}

interface PlanOption {
    type: 'personal' | 'group' | 'webinar';
    level_name: string;
    level_slug: string;
    pricing_id: number;
    format_slug: string;
    format_name?: string;
    is_recommended?: boolean;
}

interface SelectedBatch {
    batch_name: string;
    batch_code: string;
    coach_name: string;
    class_timing: string;
    class_days: string;
    start_date: string;
    tournament_id: number;
    level_name: string | null;
}

interface PlanSelectorProps {
    selectedPlan: 'personal' | 'group' | 'webinar';
    selectedPricingId?: number; // Track selected pricing ID for precise matching
    selectedLevelSlug?: string; // Track selected level slug for precise matching
    onSelect: (plan: 'personal' | 'group' | 'webinar', pricingId?: number, levelName?: string) => void;
    onSelectTiming?: () => void;
    onViewWebinarDetails?: () => void; // Handler for viewing webinar details
    isMobile: boolean;
    planPerks?: string[]; // Changed to array since it comes from selectedPricing
    recommendedPlan?: 'personal' | 'group' | 'webinar' | null; // Recommended/current plan
    availablePlanTypes?: ('personal' | 'group' | 'webinar')[]; // Available plan types from API (for backward compatibility)
    availablePlanOptions?: PlanOption[]; // Individual plan options with level info
    webinarOptions?: WebinarOption[]; // Webinar batches data to show in webinar card
    selectedBatch?: SelectedBatch | null; // Selected batch for group plans
    /** When false, do not show the "Current" tag on the recommended plan (e.g. for new-user flow) */
    showCurrentTag?: boolean;
    /** When true (new-user plan flow), show the selected plan first; otherwise show recommended first */
    prioritizeSelectedAtTop?: boolean;
    isFoundationPreference?: boolean;
}

const PlanSelector: React.FC<PlanSelectorProps> = ({ 
    selectedPlan, 
    selectedPricingId,
    selectedLevelSlug,
    onSelect, 
    onSelectTiming,
    onViewWebinarDetails,
    isMobile, 
    planPerks, 
    recommendedPlan, 
    availablePlanTypes = [], 
    availablePlanOptions = [],
    webinarOptions = [],
    selectedBatch = null,
    showCurrentTag = true,
    prioritizeSelectedAtTop = false,
    isFoundationPreference = false,
}) => {
    const [showMore, setShowMore] = React.useState(false);
    
    // Default perks if not provided from API
    const defaultPlanPerks: string[] = [];
    
    const perks = planPerks || defaultPlanPerks;
    
    // Helper to get plan type label
    const getPlanTypeLabel = (type: 'personal' | 'group' | 'webinar', formatSlug?: string): string => {
        // Check for duel_group_2_students format (PG batches) - treated as personal
        if (formatSlug === 'duel_group_2_students') {
            return 'Personal Duel Group';
        }
        
        switch (type) {
            case 'personal': return 'Personal 1:1';
            case 'group': return 'Small Group';
            case 'webinar': return 'Masterclass';
            default: return '';
        }
    };

    // Helper to get plan type subtitle
    const getPlanTypeSubtitle = (type: 'personal' | 'group' | 'webinar', formatSlug?: string): string => {
        // Check for duel_group_2_students format (PG batches) - treated as personal
        if (formatSlug === 'duel_group_2_students') {
            return '2 students';
        }
        if (type === 'group' && (formatSlug === 'master_pride_plus' || formatSlug === 'master_pride')) {
            return '10-12 students';
        }
        
        switch (type) {
            case 'personal': return 'One-on-one';
            case 'group': return '4-8 students';
            case 'webinar': return '';
            default: return '';
        }
    };

    // Build plan options from availablePlanOptions if provided, otherwise fallback to legacy logic
    const allPlans = React.useMemo(() => {
        if (availablePlanOptions.length > 0) {
            // Use new plan options with level info
            return availablePlanOptions.map(option => {
                const baseLabel = getPlanTypeLabel(option.type, option.format_slug);
                const optionsForType = availablePlanOptions.filter(o => o.type === option.type);
                const hasMultipleLevels = optionsForType.length > 1;
                // Multiple options with same type and same level (e.g. Master Pride vs Master Pride Plus) → show format name to differentiate
                const optionsSameTypeAndLevel = availablePlanOptions.filter(
                    o => o.type === option.type && (o.level_name || '') === (option.level_name || ''),
                );
                const useFormatName = optionsSameTypeAndLevel.length > 1 && option.format_name;
                const formatPart = useFormatName ? ` (${option.format_name})` : '';

                const label = option.type === 'webinar' && option.level_name
                    ? `${option.level_name} ${baseLabel}`
                    : hasMultipleLevels
                        ? `${baseLabel}${formatPart} - ${option.level_name}`
                        : baseLabel;
                
                return {
                    type: option.type,
                    label,
                    subtitle: getPlanTypeSubtitle(option.type, option.format_slug),
                    pricing_id: option.pricing_id,
                    level_name: option.level_name,
                    level_slug: option.level_slug,
                    is_recommended: option.is_recommended,
                };
            });
        }
        
        // Fallback: Use legacy logic with hardcoded definitions
        const allPlansDefinitions: Array<{ type: 'personal' | 'group' | 'webinar'; label: string; subtitle: string }> = [
            { type: 'personal', label: 'Personal 1:1', subtitle: 'One-on-one' },
            { type: 'group', label: 'Small Group', subtitle: '4-8 students' },
            { type: 'webinar', label: 'Masterclass', subtitle: '' },
        ];
        
        return availablePlanTypes.length > 0
            ? allPlansDefinitions.filter(p => availablePlanTypes.includes(p.type))
            : allPlansDefinitions;
    }, [availablePlanOptions, availablePlanTypes]);
    
    // Recommended plan (is_recommended or recommendedPlan type)
    const recommendedPlanData =
        allPlans.find(p => {
            if ('is_recommended' in p && p.is_recommended) return true;
            return p.type === recommendedPlan;
        }) || allPlans.find(p => p.type === recommendedPlan);
    // For new-user flow: put selected plan first; otherwise use recommended first
    const selectedPlanData =
        prioritizeSelectedAtTop && selectedPricingId !== undefined
            ? allPlans.find(p => 'pricing_id' in p && p.pricing_id === selectedPricingId)
            : null;
    const firstPlan = prioritizeSelectedAtTop ? (selectedPlanData || recommendedPlanData) : recommendedPlanData;
    const otherPlans = firstPlan ? allPlans.filter(p => p !== firstPlan) : allPlans;
    
    // Render a plan card
    const renderPlanCard = (plan: typeof allPlans[0], isRecommended: boolean = false) => {
        // Check if this specific option is selected
        // First check by pricing_id if available
        // If pricing_id is not set (e.g., after coach change), check by type AND level_slug
        let isSelected = false;
        if ('pricing_id' in plan && selectedPricingId !== undefined) {
            isSelected = plan.pricing_id === selectedPricingId;
        } else if (selectedPlan === plan.type) {
            // If no pricing_id, check by type and level_slug to avoid selecting all plans of same type
            if (selectedLevelSlug && 'level_slug' in plan) {
                isSelected = plan.level_slug === selectedLevelSlug;
            } else if (!selectedLevelSlug) {
                // If no level_slug selected, fall back to type matching (for backward compatibility)
                isSelected = true;
            }
        }
        const isPersonal = plan.type === 'personal';
        const isGroup = plan.type === 'group';
        const isWebinar = plan.type === 'webinar';
        
        // For group plans, show batch details if selected, otherwise show "Select Batch" button
        // Hide "Select Batch" button if batch is already selected for group plans
        const showSlotCta = onSelectTiming && !(isGroup && !isFoundationPreference && selectedBatch && selectedBatch.batch_name);
        
        const slotCta = showSlotCta ? (
                            <Pressable
                                onPress={(e) => {
                                    e.stopPropagation();
                                    onSelectTiming();
                                }}
                style={isMobile ? styles.slotLinkMobile : styles.slotLinkDesktop}>
                <CCText
                    fontSize={FontSizes[12]}
                    fontWeight="600"
                    color={CCColors.Primary.Brown}>
                    {isGroup ? isFoundationPreference ? 'Select preferred slots' : 'Select Batch' : 'Select preferred slots'}
                </CCText>
                <MaterialCommunityIcons
                    name="chevron-right"
                    size={14}
                    color={CCColors.Primary.Brown}
                />
                            </Pressable>
        ) : null;
        
        // Show selected batch details as clickable when batch is selected (for group plans)
        const selectedBatchCta = isGroup && !isFoundationPreference && selectedBatch && onSelectTiming ? (
            <Pressable
                onPress={(e) => {
                    e.stopPropagation();
                    onSelectTiming();
                }}
                style={isMobile ? styles.slotLinkMobile : styles.slotLinkDesktop}>
                <Box flexDirection="row" alignItems="center" gap={6} flexWrap="wrap">
                    <CCText
                        fontSize={FontSizes[12]}
                        fontWeight="600"
                        color={CCColors.Primary.Brown}
                        numberOfLines={1}>
                        {selectedBatch.batch_name}
                    </CCText>
                    {selectedBatch.coach_name && (
                        <>
                            <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey}>
                                •
                            </CCText>
                            <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey} numberOfLines={1}>
                                {selectedBatch.coach_name}
                            </CCText>
                        </>
                    )}
                    {selectedBatch.class_days && (
                        <>
                            <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey}>
                                •
                            </CCText>
                            <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey} numberOfLines={1}>
                                {selectedBatch.class_days.split(',').map(d => {
                                    const dayMap: {[key: string]: string} = {
                                        '1': 'Mon', '2': 'Tue', '3': 'Wed',
                                        '4': 'Thu', '5': 'Fri', '6': 'Sat', '7': 'Sun'
                                    };
                                    return dayMap[d.trim()] || d.trim();
                                }).join(', ')}
                            </CCText>
                        </>
                    )}
                    {selectedBatch.class_timing && (
                        <>
                            <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey}>
                                •
                            </CCText>
                            <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey} numberOfLines={1}>
                                {selectedBatch.class_timing.split(',').slice(0, 1).join(', ')}
                                {selectedBatch.class_timing.split(',').length > 1 ? '...' : ''}
                            </CCText>
                        </>
                    )}
                </Box>
                <MaterialCommunityIcons
                    name="chevron-right"
                    size={14}
                    color={CCColors.Primary.Brown}
                />
            </Pressable>
        ) : null;

        const webinarDetailsCta = onViewWebinarDetails ? (
            <Pressable
                onPress={(e) => {
                    e.stopPropagation();
                    onViewWebinarDetails();
                }}
                style={isMobile ? styles.slotLinkMobile : styles.slotLinkDesktop}>
                <CCText
                    fontSize={FontSizes[12]}
                    fontWeight="600"
                    color={CCColors.Primary.Brown}>
                    View Details
                </CCText>
                <MaterialCommunityIcons
                    name="chevron-right"
                    size={14}
                    color={CCColors.Primary.Brown}
                />
            </Pressable>
        ) : null;

        return (
            <Pressable
                key={'pricing_id' in plan ? `plan-${plan.pricing_id}` : `plan-${plan.type}`}
                style={[
                    styles.planCard,
                    isSelected && styles.planCardSelected,
                ]}
                onPress={() => {
                    const pricingId = 'pricing_id' in plan ? plan.pricing_id : undefined;
                    const levelName = 'level_name' in plan ? plan.level_name : undefined;
                    onSelect(plan.type, pricingId, levelName);
                }}>
                {isSelected && (isPersonal || isGroup) && !isMobile && (selectedBatchCta || slotCta)}
                {isSelected && isWebinar && onViewWebinarDetails && !isMobile && webinarDetailsCta}
                <Box flexDirection="row" alignItems="center" gap={12} marginBottom={isSelected ? 12 : 8}>
                    {isSelected ? (
                                <Box
                                    width={24}
                                    height={24}
                                    borderRadius={12}
                                    backgroundColor={CCColors.Primary.Brown}
                                    justifyContent="center"
                                    alignItems="center">
                                    <MaterialCommunityIcons name="check" size={16} color={CCColors.White} />
                                </Box>
                            ) : (
                                <Box
                                    width={24}
                                    height={24}
                                    borderRadius={12}
                                    borderWidth={2}
                                    borderColor={CCColors.Grey.Grey}
                                />
                            )}
                            <CCText fontSize={FontSizes[16]} fontWeight="bold" color={CCColors.TextColor.Dark}>
                        {plan.label}
                            </CCText>
                            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey}>
                        {plan.subtitle}
                    </CCText>
                    {isRecommended && showCurrentTag && (
                        <Box
                            backgroundColor={CCColors.Green}
                            paddingHorizontal={8}
                            paddingVertical={4}
                            borderRadius={4}
                            marginLeft={8}>
                            <CCText fontSize={FontSizes[11]} fontWeight="600" color={CCColors.White}>
                                Current
                            </CCText>
                        </Box>
                    )}
                </Box>
                {isSelected && (isPersonal || isGroup) && isMobile && (slotCta || selectedBatchCta) && (
                    <Box marginTop={8}>
                        {slotCta || selectedBatchCta}
                    </Box>
                )}
                {isSelected && isWebinar && onViewWebinarDetails && isMobile && (
                    <Box marginTop={8}>
                        {webinarDetailsCta}
                    </Box>
                )}

                {/* Show perks when selected (for all plans including webinar) */}
                {isSelected && perks.length > 0 && (
                            <Box marginLeft={36} marginTop={4}>
                        {perks.map((perk, index) => (
                                    <Box key={index} flexDirection="row" alignItems="center" marginBottom={8}>
                                        <Box
                                            width={16}
                                            height={16}
                                            borderRadius={8}
                                            backgroundColor={CCColors.Green}
                                            justifyContent="center"
                                            alignItems="center"
                                            marginRight={8}>
                                            <MaterialCommunityIcons 
                                                name="check" 
                                                size={10} 
                                                color={CCColors.White} 
                                            />
                                        </Box>
                                        <CCText 
                                            fontSize={FontSizes[13]} 
                                            color={CCColors.TextColor.Dark}
                                            flex={1}>
                                            {perk}
                                        </CCText>
                                    </Box>
                                ))}
                            </Box>
                        )}
                
              
                </Pressable>
        );
    };

    return (
        <Box marginBottom={30}>
            <Box flexDirection="column" gap={15}>
                {/* Show selected (or recommended) plan first; "Current" tag only when it's the recommended plan */}
                {firstPlan && (
                    <Box style={styles.planCardContainer}>
                        {renderPlanCard(firstPlan, showCurrentTag && firstPlan === recommendedPlanData)}
                    </Box>
                )}

                {/* Show other plans in "View More" section */}
                {otherPlans.length > 0 && (
                    <>
                        {showMore ? (
                            <>
                                {otherPlans.map(plan => (
                                    <Box key={'pricing_id' in plan ? `plan-${plan.pricing_id}` : `plan-${plan.type}`} style={styles.planCardContainer}>
                                        {renderPlanCard(plan, false)}
                                    </Box>
                                ))}
                                <Pressable
                                    onPress={() => setShowMore(false)}
                                    style={styles.viewMoreButton}>
                                    <CCText fontSize={FontSizes[14]} fontWeight="600" color={CCColors.Primary.Brown}>
                                        View Less
                                    </CCText>
                                    <MaterialCommunityIcons 
                                        name="chevron-up" 
                                        size={20} 
                                        color={CCColors.Primary.Brown} 
                                    />
                                </Pressable>
                            </>
                        ) : (
                            <Pressable
                                onPress={() => setShowMore(true)}
                                style={styles.viewMoreButton}>
                                <CCText fontSize={FontSizes[14]} fontWeight="600" color={CCColors.Primary.Brown}>
                                    View More Plans ({otherPlans.length})
                                </CCText>
                                <MaterialCommunityIcons 
                                    name="chevron-down" 
                                    size={20} 
                                    color={CCColors.Primary.Brown} 
                                />
                            </Pressable>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

const styles = StyleSheet.create({
    planCardContainer: {
        position: 'relative',
    },
    planCard: {
        backgroundColor: CCColors.White,
        padding: 20,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: CCColors.Grey.Divider200,
    },
    planCardSelected: {
        backgroundColor: CCColors.White,
        borderColor: CCColors.TextColor.Dark,
        borderWidth: 1,
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 0,
        },
        shadowOpacity: 0.2,
        shadowRadius: 10,
        elevation: 8,
    },
    slotLinkDesktop: {
        position: 'absolute',
        top: 12,
        right: 12,
        zIndex: 10,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 10,
        backgroundColor: CCColors.Primary.lightBrown,
        borderWidth: 1,
        borderColor: CCColors.Primary.Brown,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    slotLinkMobile: {
        alignSelf: 'flex-start',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 10,
        backgroundColor: CCColors.Primary.lightBrown,
        borderWidth: 1,
        borderColor: CCColors.Primary.Brown,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    viewMoreButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: 16,
        backgroundColor: CCColors.White,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: CCColors.Primary.Brown,
        borderStyle: 'dashed',
    },
});

export default PlanSelector;
